import React from 'react';
import { CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, approaches, progressMetrics } from '../mock';
import WhatsAppButton from '../components/WhatsAppButton';
import { Section } from '../components/ui/section';
import { useLanguage } from '../LanguageContext';
import '../styles/harvest.css';

const Services = () => {
  const { t, language } = useLanguage();
  
  const serviceKeys = ['speechLanguage', 'attentionFocus', 'socialSkills', 'emotionalRegulation', 'academicSkills', 'sensoryIntegration'];

  return (
    <div className="fade-in">
      {/* Split Hero Section */}
      <section className="relative w-full min-h-screen flex" dir="ltr">
        {/* Left Side - Text Content with Gradient */}
        <div className="w-full lg:w-1/2 min-h-screen flex flex-col justify-center px-8 md:px-16 lg:px-20 relative z-10"
             style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #4A5568 50%, #718096 100%)' }}>
          <div className={`max-w-[500px] ${language === 'ar' ? 'text-right ml-auto lg:mr-0' : 'ml-auto lg:ml-0'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              {t('services.heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed">
              {t('services.heroSubtitle')}
            </p>
            <a 
              href="#our-programs" 
              className="inline-block px-8 py-4 bg-brand-orange hover:bg-[#A34D24] text-white rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {t('services.explorePrograms')}
            </a>
          </div>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-3xl animate-bounce">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="8 12 12 16 16 12"/>
            </svg>
          </div>
        </div>
        
        {/* Right Side - Image */}
        <div className="hidden lg:block w-1/2 min-h-screen relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_harvest-special-ed/artifacts/2itl9rno_Programs.jpeg"
            alt="Programs at Harvest Education Consulting"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient overlay from left */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#4A5568] to-transparent w-1/3"></div>
          
          {/* Decorative sparkle */}
          <div className="absolute bottom-8 right-8 text-white/30">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"/>
            </svg>
          </div>
        </div>
        
        {/* Mobile Background Image */}
        <div className="lg:hidden absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_harvest-special-ed/artifacts/2itl9rno_Programs.jpeg"
            alt="Programs at Harvest Education Consulting"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
      </section>

      {/* Services Grid */}
      <Section variant="white">
        <div className="container" id="our-programs">
          <div className="services-grid">
            {services.map((service, idx) => {
              const IconComponent = service.icon ? LucideIcons[service.icon] : null;
              const titleKey = `services.${serviceKeys[idx]}`;
              const descKey = `services.${serviceKeys[idx]}Desc`;
              const detailKeys = ['speech', 'attention', 'social', 'emotional', 'academic', 'sensory'];
              const serviceDetailKey = detailKeys[idx];
              
              return (
                <div key={service.id} className={`service-card ${service.color} ${language === 'ar' ? 'text-right' : ''}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                  <div className="mb-4">
                    {service.imageUrl ? (
                      <img src={service.imageUrl} alt={t(titleKey)} className="w-[54px] h-[54px] object-contain" />
                    ) : IconComponent ? (
                      <IconComponent size={36} className="text-gray-700" />
                    ) : null}
                  </div>
                  <h3 className="heading-2 mb-3">{t(titleKey)}</h3>
                  <p className="body-medium mb-4">{t(descKey)}</p>
                  <div className="mt-auto">
                    <h4 className="body-small font-semibold mb-2">{t('services.whatWeAddress')}</h4>
                    <ul className="space-y-2">
                      {t(`serviceDetails.${serviceDetailKey}`).map((detail, detailIdx) => (
                        <li key={detailIdx} className="flex items-start gap-2 body-small">
                          <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Evidence-Based Approach Section */}
      <Section variant="beige">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className={`text-center mb-5 ${language === 'ar' ? 'text-right' : ''}`}>
              <h2 className="heading-1 mb-3">{t('services.evidenceTitle')}</h2>
              <p className="body-large" style={{ maxWidth: '800px', margin: language === 'ar' ? '0 0 0 auto' : '0 auto' }}>
                {t('services.evidenceSubtitle')}
              </p>
            </div>

            <div className="space-y-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
              {['iep', 'aba', 'speech', 'ot', 'social'].map((key, index) => {
                const icons = ['ClipboardList', 'TrendingUp', 'MessageSquare', 'Hand', 'Smile'];
                const IconComponent = LucideIcons[icons[index]];
                return (
                  <div key={index} className="bg-white rounded-lg p-6 hover-lift">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex-shrink-0">
                        {IconComponent && <IconComponent size={32} className="text-gray-700" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="heading-3 mb-2">{t(`approaches.${key}`)}</h3>
                        <p className="body-medium">{t(`approaches.${key}Desc`)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* Progress Tracking Section */}
      <Section variant="white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-8 md:p-12" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }}>
              <h2 className="heading-1 mb-3 text-center">{t('services.progressTitle')}</h2>
              <p className="body-large text-center mb-5">
                {t('services.progressSubtitle')}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[t('home.progressItem1'), t('home.progressItem2'), t('home.progressItem3'), t('home.progressItem4')].map((metric, index) => (
                  <div key={index} className={`flex items-start gap-3 p-4 bg-gray-50 rounded-lg ${language === 'ar' ? 'flex-row-reverse text-right' : ''}`}>
                    <CheckCircle size={24} className="text-gray-700 mt-0.5 flex-shrink-0" />
                    <p className="body-medium">{metric}</p>
                  </div>
                ))}
              </div>

              <div className={`bg-gray-50 rounded-lg p-6 mt-5 ${language === 'ar' ? 'text-right' : ''}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                <h3 className="heading-3 mb-3">{t('services.parentPartnership')}</h3>
                <p className="body-medium mb-3">
                  {t('services.parentPartnershipText')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                    <span className="body-medium">{t('services.parentCoaching')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                    <span className="body-medium">{t('services.homeStrategies')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                    <span className="body-medium">{t('services.openFeedback')}</span>
                  </li>
                </ul>
                <p className="body-large mt-4 font-semibold text-center">
                  {t('services.partnershipNote')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section variant="beige">
        <div className="container">
          <div className="bg-white rounded-lg p-8 md:p-12 text-center max-w-3xl mx-auto" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }}>
            <h2 className="heading-1 mb-3">{t('services.ctaTitle')}</h2>
            <p className="body-large mb-5">
              {t('services.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <WhatsAppButton />
              <a href="/contact#book-consultation" className="btn-secondary">
                {t('home.scheduleFree')}
              </a>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Services;
